import { AloftPrice } from '@aloft/toolkit-shared';
import { Text, View } from '@react-pdf/renderer';

import { type ReactNode } from 'react';

import { salesComparisonPageStyles as styles } from '../styles.ts';
import {
  AdditionalFeatureKey,
  SalesComparisonAdjustmentKey,
  SalesComparisonGridColumnData,
} from '../types.ts';
import { getSalesComparisonNestedValue } from '../utils/getSalesComparisonNestedValue.ts';

const Label = ({ text }: { text: string[] }) => {
  const labels = text.map((t) => (
    <View style={{ ...styles.label }} key={`label-${text.join('-')}`}>
      <Text style={styles.text}>{t}</Text>
    </View>
  ));

  return <View style={styles.column}>{labels}</View>;
};

const Value = ({
  comp,
  keys,
  align,
  emptyFallback,
}: {
  comp: SalesComparisonGridColumnData | null;
  keys: (keyof SalesComparisonGridColumnData | AdditionalFeatureKey)[];
  align: 'left' | 'right' | 'center';
  emptyFallback?: string;
}) => {
  const values = keys.map((t) => {
    const raw = getSalesComparisonNestedValue(comp, t);
    const text = raw?.trim() || emptyFallback || raw;
    return (
      <View style={{ ...styles.value, textAlign: align }} key={`label-${keys.join('-')}`}>
        <Text style={styles.text}>{text}</Text>
      </View>
    );
  });

  return <View style={styles.column}>{values}</View>;
};

const Adjustment = ({
  subject,
  comp,
  fields,
  adjustment,
}: {
  subject: SalesComparisonGridColumnData;
  comp: SalesComparisonGridColumnData;
  fields: (keyof SalesComparisonGridColumnData | AdditionalFeatureKey)[];
  adjustment: SalesComparisonAdjustmentKey;
}) => {
  const hasValueDifference = fields.some((k) => {
    const subjectValue = getSalesComparisonNestedValue(subject, k);
    const compValue = getSalesComparisonNestedValue(comp, k);
    return subjectValue && compValue && subjectValue !== compValue;
  });

  const rawAdjustment = comp?.adjustments?.[adjustment] ?? AloftPrice.zero;
  // Only display $0 adjustments if there is an explicit zero adjustment or if there is a value difference between the subject and comp for the adjusted field(s). This is to avoid confusion around $0 adjustments when there is no actual adjustment being made.
  const displayAdjustment =
    rawAdjustment.equals(AloftPrice.zero) &&
    comp?.explicitZeroAdjustments?.[adjustment] &&
    hasValueDifference
      ? '$0'
      : rawAdjustment.toString(true);

  const negativeAdjustment = displayAdjustment?.startsWith('-');
  return (
    <View style={styles.column}>
      <View style={styles.value}>
        <Text
          style={{
            ...styles.text,
            ...(negativeAdjustment ? styles.negativeAdjustment : styles.positiveAdjustment),
            textAlign: 'right',
          }}
        >
          {displayAdjustment}
        </Text>
      </View>
    </View>
  );
};

const ManualCostCompCostColumn = ({ comp }: { comp: SalesComparisonGridColumnData | null }) => {
  const raw = getSalesComparisonNestedValue(comp, 'manualCostSummary');
  const emphasize = raw && raw !== 'None' && !raw.startsWith('-');
  return (
    <View style={styles.column}>
      <View style={{ ...styles.value, textAlign: 'right' }}>
        <Text
          style={{
            ...styles.text,
            textAlign: 'right',
            ...(emphasize ? styles.positiveAdjustment : {}),
          }}
        >
          {raw}
        </Text>
      </View>
    </View>
  );
};

export const SalesComparisonRow = ({
  fields,
  labels,
  subject,
  comparables,
  adjustment,
  borderTop = false,
  bold = false,
  align = 'left',
}: {
  fields: (keyof SalesComparisonGridColumnData | AdditionalFeatureKey)[];
  labels: string[];
  subject: SalesComparisonGridColumnData;
  comparables: SalesComparisonGridColumnData[];
  adjustment?: SalesComparisonAdjustmentKey;
  borderTop?: boolean;
  bold?: boolean;
  align?: 'left' | 'right' | 'center';
}) => {
  const isManualCostRow =
    adjustment === 'manualCostAdjustment' ||
    (fields.length === 1 && fields[0] === 'manualCostSummary');
  const isOtherRow = adjustment === 'otherAdjustments';
  const otherLabel = subject.otherAdjustmentsDisplayName?.trim() || 'Other';
  const rowLabels = isManualCostRow ? ['Manual Cost'] : isOtherRow ? [otherLabel] : labels;
  const isSummaryRow = fields.includes('netAdj') || fields.includes('adjSalesPrice');
  const subjectShowsAdjustmentValue =
    adjustment === 'totalCostToCureAdjustment' || isOtherRow;

  return (
    <View
      wrap={isSummaryRow ? false : undefined}
      style={{
        ...styles.row,
        ...(bold ? styles.bold : {}),
      }}
    >
      {[subject, ...comparables].map((comp, i) => {
        let valueBlock: ReactNode;
        if (subjectShowsAdjustmentValue && i === 0 && adjustment) {
          valueBlock = (
            <Adjustment subject={subject} comp={comp} fields={fields} adjustment={adjustment} />
          );
        } else if (isManualCostRow && i === 0) {
          valueBlock = <Value comp={comp} keys={['manualCostDisplayName']} align="left" />;
        } else if (isManualCostRow && i !== 0) {
          valueBlock = (
            <>
              <Value comp={comp} keys={['manualCostDisplayName']} align="left" />
              <ManualCostCompCostColumn comp={comp} />
            </>
          );
        } else {
          valueBlock = (
            <Value
              comp={comp}
              keys={fields}
              align={align}
              emptyFallback={isOtherRow ? 'None' : undefined}
            />
          );
        }

        return (
          <View
            style={{
              ...styles.innerRow,
              ...(borderTop ? styles.borderTop : {}),
            }}
            key={`scc-${fields.join('-')}-${comp?.address}`}
          >
            {i === 0 && <Label text={rowLabels} />}
            {valueBlock}
            {i !== 0 && adjustment && !isManualCostRow && (
              <Adjustment subject={subject} comp={comp} fields={fields} adjustment={adjustment} />
            )}
          </View>
        );
      })}
    </View>
  );
};
